import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import DataTable from "react-data-table-component";
import apiurl from "../Api";

function OcrReport() {
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [caseOptions, setCaseOptions] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [reportByCase, setReportByCase] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const pageSizeOptions = useMemo(() => ([
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: 200, label: "200" },
  ]), []);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [hasReport, setHasReport] = useState(false);

  const formatDocTypeLabel = (value) => {
    const str = (value || "").toString().trim();
    if (!str) return "";
    const cleaned = str.replace(/[^a-zA-Z0-9]+/g, " ").trim();
    if (!cleaned) return "";
    return cleaned
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  };

  const fetchTypes = async () => {
    try {
      const res = await axios.get(`${apiurl}ocr_report.php?action=types`);
      const list = res.data?.types || [];
      const opts = [];
      list.forEach((t) => opts.push({ value: t, label: formatDocTypeLabel(t) || t }));
      setTypes(opts);
      setSelectedType(null);
    } catch (err) {
      setErrorMsg("Failed to load document types.");
    }
  };

  const fetchReport = async (overridePage = page, opts = {}) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const useSearch = opts.useSearch === true;
      const searchValue = useSearch ? (opts.search ?? searchInput) : "";
      const staffFilterValue = Object.prototype.hasOwnProperty.call(opts, "staffAccessToken")
        ? (opts.staffAccessToken || "")
        : (selectedStaff?.value || "");
      const caseFilterValue = Object.prototype.hasOwnProperty.call(opts, "caseId")
        ? (opts.caseId || "")
        : (reportByCase ? (selectedCase?.value || "") : "");
      const payload = {
        document_type: selectedType?.value || "",
        from_date: fromDate,
        to_date: toDate,
        search: searchValue,
        case_id: caseFilterValue,
        staff_number: staffFilterValue,
        staff_access_token: staffFilterValue,
        page: overridePage,
        page_size: pageSize,
      };
      const res = await axios.post(`${apiurl}ocr_report.php`, payload);
      if (!res.data?.status) {
        throw new Error(res.data?.message || "Request failed");
      }
      setHeaders(res.data.headers || []);
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || overridePage);
      setHasReport(true);
    } catch (err) {
      setErrorMsg(err.message || "Failed to fetch report.");
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffOptions = async (filters = {}) => {
    try {
      const qs = new URLSearchParams();
      qs.set("action", "staffs");
      const dt = filters.documentType ?? selectedType?.value ?? "";
      const fd = filters.fromDate ?? fromDate;
      const td = filters.toDate ?? toDate;
      if (dt) qs.set("document_type", dt);
      if (fd) qs.set("from_date", fd);
      if (td) qs.set("to_date", td);

      const res = await axios.get(`${apiurl}ocr_report.php?${qs.toString()}`);
      const list = res.data?.staffs || [];
      const mapped = list.map((s) => ({
        value: s.number,
        label: `${s.name || "Unknown"}${s.number ? ` (${s.number})` : ""}`,
      }));
      setStaffOptions(mapped);
      setSelectedStaff((prev) => {
        if (!prev) return null;
        const stillExists = mapped.some((opt) => opt.value === prev.value);
        return stillExists ? prev : null;
      });
    } catch (err) {
      setStaffOptions([]);
      setSelectedStaff(null);
    }
  };

  const fetchCaseOptions = async (filters = {}) => {
    try {
      const qs = new URLSearchParams();
      qs.set("action", "cases");
      const dt = filters.documentType ?? selectedType?.value ?? "";
      const fd = filters.fromDate ?? fromDate;
      const td = filters.toDate ?? toDate;
      const staffFilterValue = Object.prototype.hasOwnProperty.call(filters, "staffNumber")
        ? (filters.staffNumber || "")
        : (selectedStaff?.value || "");
      if (dt) qs.set("document_type", dt);
      if (fd) qs.set("from_date", fd);
      if (td) qs.set("to_date", td);
      if (staffFilterValue) qs.set("staff_number", staffFilterValue);

      const res = await axios.get(`${apiurl}ocr_report.php?${qs.toString()}`);
      const list = res.data?.cases || [];
      const mapped = list.map((c) => ({
        value: c.case_id,
        label: c.case_name || "Unnamed Case",
      }));
      setCaseOptions(mapped);
      setSelectedCase((prev) => {
        if (!prev) return null;
        const stillExists = mapped.some((opt) => opt.value === prev.value);
        return stillExists ? prev : null;
      });
    } catch (err) {
      setCaseOptions([]);
      setSelectedCase(null);
    }
  };

  useEffect(() => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 30);
    setFromDate(from.toISOString().split("T")[0]);
    setToDate(today.toISOString().split("T")[0]);
    fetchTypes();
  }, []);

  useEffect(() => {
    if (!fromDate || !toDate) return;
    fetchStaffOptions();
    fetchCaseOptions();
  }, [selectedType, fromDate, toDate]);

  useEffect(() => {
    if (!hasReport) return;
    const q = searchInput.trim();
    const t = setTimeout(() => {
      fetchReport(1, {
        useSearch: q !== "",
        search: q,
        caseId: reportByCase ? (selectedCase?.value || "") : "",
      });
    }, 450);
    return () => clearTimeout(t);
  }, [searchInput, reportByCase, selectedCase, hasReport]);
 
  const columns = useMemo(() => {
    const emptyLabel = "-";
    const baseCols = [
      {
        name: "S.No",
        selector: (_, index) => (page - 1) * pageSize + index + 1,
        sortable: false,
        width: "80px",
      },
      {
        name: "Document Type",
        selector: (row) => {
          const raw = row.document_type || "";
          return formatDocTypeLabel(raw) || raw || emptyLabel;
        },
        sortable: true,
        width: "180px",
      },
      { name: "Case", selector: (row) => row.case_name || emptyLabel, sortable: true, width: "170px" },
      { name: "Created By", selector: (row) => row.created_by || emptyLabel, sortable: true, width: "180px" },
      { name: "Created At", selector: (row) => row.created_at || emptyLabel, sortable: true, width: "160px" },
    ];

    const dynamicCols = headers.map((h) => ({
      name: h,
      selector: (row) => row[h] || emptyLabel,
      sortable: true,
      wrap: true,
      minWidth: "220px",
    }));

    return [...baseCols, ...dynamicCols];
  }, [headers, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <div className="main-content app-content">
        <div className="container-fluid" style={{ marginTop: "20px" }}>
          <div className="card custom-card shadow-lg mb-3">
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                <h3 className="mb-0 fw-bold text-lg" style={{ fontSize: "1.75rem", lineHeight: "1.2" }}>
                  OCR Report
                </h3>
                <div
                  className="border rounded-3 px-3 py-2 bg-light"
                  style={{ maxWidth: "1100px", flex: "1 1 900px" }}
                >
                  <div className="text-muted fw-semibold mb-2" style={{ fontSize: "0.82rem" }}>
                    To add documents here, send the document to the below WhatsApp number or Email id from your registered email ID or the mobile number you use to log in.
                  </div>
                  <div className="d-flex flex-wrap align-items-center justify-content-center gap-3">
                    <span className="d-inline-flex align-items-center gap-2 fw-semibold text-dark">
                      <i className="ri-mail-line text-primary" style={{ fontSize: "1.1rem" }}></i>
                      documents@meraquii.com
                    </span>
                    <span className="d-inline-flex align-items-center gap-2 fw-semibold text-dark">
                      <i className="ri-whatsapp-line text-success" style={{ fontSize: "1.1rem" }}></i>
                      +91 92204 43680
                    </span>
                  </div>
                </div>
              </div>
              <div className="row g-3 mt-2">
                <div className="col-12 col-md-4 col-lg-3">
                  <label className="form-label">Document Type</label>
                  <Select
                    options={types}
                    value={selectedType}
                    onChange={(opt) => setSelectedType(opt)}
                    isSearchable
                    placeholder="Select document type"
                  />
                </div>
                <div className="col-6 col-md-2 col-lg-2">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-2 col-lg-2">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-1 col-lg-1">
                  <label className="form-label">Page Size</label>
                  <Select
                    options={pageSizeOptions}
                    value={pageSizeOptions.find((o) => o.value === pageSize)}
                    onChange={(opt) => setPageSize(opt?.value || 25)}
                    isSearchable={false}
                  />
                </div>
                <div className="col-12 col-md-3 col-lg-4 d-flex align-items-end">
                  <button
                    className="btn btn-primary w-100 py-2 fw-semibold ocr-generate-btn"
                    style={{ fontSize: "0.95rem" }}
                    disabled={loading}
                    onClick={() => {
                      if (!selectedType?.value) {
                        setErrorMsg("Document type is required");
                        setRows([]);
                        setHeaders([]);
                        setTotal(0);
                        setPage(1);
                        setHasReport(false);
                        return;
                      }
                      setErrorMsg("");
                      setReportByCase(false);
                      fetchReport(1, { useSearch: false, caseId: "" });
                    }}
                  >
                    {loading ? "Generating..." : "Generate Report"}
                  </button>
                </div>
              </div>
              {errorMsg && <div className="alert alert-danger mt-3 mb-0">{errorMsg}</div>}
            </div>
          </div>

          <div className="card custom-card shadow-lg mt-3">
            <div className="card-body">
              <style>{`
                .ocr-spinner {
                  width: 32px;
                  height: 32px;
                  border: 4px solid #e5e7eb;
                  border-top-color: #2563eb;
                  border-radius: 50%;
                  animation: ocr-spin 0.8s linear infinite;
                  margin: 12px auto;
                }
                .ocr-table .rdt_TableHeadRow {
                  background: var(--primary-color, #0d6efd);
                }
                .ocr-table .rdt_TableCol {
                  color: #ffffff;
                  font-weight: 600;
                }
                .ocr-table-wrap {
                  position: relative;
                }
                .ocr-table-overlay {
                  position: absolute;
                  inset: 0;
                  background: rgba(255, 255, 255, 0.65);
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  z-index: 5;
                }
                .ocr-generate-btn {
                  transition: box-shadow 0.2s ease, transform 0.2s ease;
                }
                .ocr-generate-btn:hover {
                  box-shadow: 0 0.85rem 1.5rem rgba(13, 110, 253, 0.28);
                  transform: translateY(-1px);
                }
                @keyframes ocr-spin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
              <div className="row g-2 align-items-end mb-3">
                <div className="col-12 col-lg-4">
                  <Select
                    options={caseOptions}
                    value={selectedCase}
                    onChange={(opt) => {
                      const picked = opt || null;
                      setSelectedCase(picked);
                      if (!picked) {
                        setReportByCase(false);
                        setRows([]);
                        setHeaders([]);
                        setTotal(0);
                        setPage(1);
                        setHasReport(false);
                        setErrorMsg("");
                        return;
                      }
                      setReportByCase(true);
                      fetchReport(1, {
                        useSearch: searchInput.trim() !== "",
                        search: searchInput,
                        caseId: picked.value,
                      });
                    }}
                    isSearchable
                    isClearable
                    placeholder="Filter by candidate"
                  />
                </div>
                <div className="col-12 col-lg-8">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by attribute key/value"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && hasReport) {
                          const q = searchInput.trim();
                          fetchReport(1, {
                            useSearch: q !== "",
                            search: q,
                            caseId: reportByCase ? (selectedCase?.value || "") : "",
                          });
                        }
                      }}
                      disabled={!hasReport}
                    />
                    <button
                      className="btn btn-light"
                      onClick={() => {
                        setSearchInput("");
                        fetchReport(1, { useSearch: false });
                      }}
                      disabled={!hasReport}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
              <div className="ocr-table mt-3">
                <div className="ocr-table-wrap">
                  {loading && (
                    <div className="ocr-table-overlay">
                      <div className="ocr-spinner" />
                    </div>
                  )}
                  <DataTable
                    columns={columns}
                    data={rows}
                    pagination
                    paginationServer
                    paginationTotalRows={total}
                    paginationPerPage={pageSize}
                    paginationDefaultPage={page}
                    onChangePage={(p) => fetchReport(p)}
                    onChangeRowsPerPage={(newSize) => {
                      setPageSize(newSize);
                      fetchReport(1);
                    }}
                    dense
                    highlightOnHover
                    persistTableHead
                  />
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <span className="text-muted">
                  Page {page} of {totalPages} | Total: {total}
                </span>
                <div className="btn-group">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => fetchReport(Math.max(1, page - 1))}
                    disabled={page <= 1 || loading}
                  >
                    Prev
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => fetchReport(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages || loading}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OcrReport;
